import React from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import closeIcon from "@iconify-icons/eva/close-fill";
import checkCircleIcon from "@iconify-icons/eva/checkmark-circle-2-fill";
import creditCardIcon from "@iconify-icons/eva/credit-card-fill";
import cashIcon from "@iconify-icons/eva/shopping-bag-fill";
import { alpha, useTheme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "src/redux/store";
import { clearLastPayment } from "src/redux/slices/session";
import { fCurrency } from "src/utils/formatNumber";
import useResponsive from "src/hooks/useResponsive";

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatDuration(checkInTime) {
  const ms = Math.max(0, Date.now() - new Date(checkInTime + "Z").getTime());
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function getShiftHours(checkInTime) {
  return (Date.now() - new Date(checkInTime + "Z").getTime()) / 3600000;
}

// Devuelve el BreakLog activo (sin BreakEnd) del turno actual
function getActiveBreak(clocking) {
  const breaks = clocking?.currentShift?.breaks;
  if (!breaks) return null;
  return breaks.find((b) => b.started && !b.ended) ?? null;
}

// Formatea mm:ss desde una fecha de inicio
function formatBreakDuration(breakStart) {
  if (!breakStart) return "--:--";
  const ms = Math.max(0, Date.now() - new Date(breakStart + "Z").getTime());
  const totalSecs = Math.floor(ms / 1000);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Devuelve el estilo y la info del chip según el estado del empleado
// Scheduled shift duration in hours (from WorkScheduleItem start/end)
function getScheduledHours(clocking) {
  if (!clocking?.start || !clocking?.end) return 8; // fallback
  return Math.max(
    1,
    (new Date(clocking.end + "Z") - new Date(clocking.start + "Z")) / 3600000,
  );
}

function getClockingStyle(clocking) {
  const onBreak = clocking?.currentShift?.breakInProgress;
  const shouldFinish = clocking?.currentShift?.shouldServiceBeFinish;
  const hours = getShiftHours(clocking.currentShift.checkInTime);
  const scheduled = getScheduledHours(clocking);

  if (onBreak)
    return {
      bg: "#fff7e6",
      border: "#fa8c1688",
      text: "#d46b08",
      dot: "#fa8c16",
      label: "Descanso",
    };
  if (shouldFinish || hours >= scheduled + 2)
    return {
      bg: "#fff1f0",
      border: "#ff4d4f88",
      text: "#cf1322",
      dot: "#ff4d4f",
      label: null,
    };
  if (hours >= scheduled)
    return {
      bg: "#fffbe6",
      border: "#ffc53d88",
      text: "#d48806",
      dot: "#faad14",
      label: null,
    };
  return {
    bg: "transparent",
    border: "#00000022",
    text: "inherit",
    dot: "#1890ff",
    label: null,
  };
}

function elapsedLabel(timestamp) {
  const secs = Math.floor((Date.now() - timestamp) / 1000);
  if (secs < 60) return "ahora";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `hace ${mins}m`;
  return `hace ${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function getMethodInfo(p) {
  const hasCard = Number(p.card) > 0;
  const hasCash = Number(p.cash) > 0;
  // Mixto gana sobre cualquier otro tipo cuando hay card+cash en el
  // mismo cobro. Los métodos custom se contabilizan como `card` en el
  // payload (ver buildPaidPayload en PaymentDialog), así que un cobro
  // custom+efectivo también entra aquí — sin esto, getMethodInfo
  // devolvía el nombre custom y el desglose nunca se mostraba.
  if (hasCard && hasCash) return { label: "Mixto", type: "mixed" };
  if (p.paymentMethodName) return { label: p.paymentMethodName, type: "alt" };
  if (hasCard) return { label: "Tarjeta", type: "card" };
  return { label: "Efectivo", type: "cash" };
}

// Chip compacto para el desglose de mixto. Encapsula color + label +
// amount con tipografía coherente; usado en el InfoStrip para mostrar
// cuánto fue por tarjeta y cuánto por efectivo de un cobro mixto.
function MethodChip({ label, amount, palette }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 0.5,
        px: 0.75,
        py: 0.125,
        borderRadius: 0.75,
        bgcolor: alpha(palette.main, 0.12),
        border: `1px solid ${alpha(palette.main, 0.30)}`,
        lineHeight: 1.2,
        flexShrink: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: palette.dark,
          fontWeight: 700,
          fontSize: "0.7rem",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: palette.dark,
          fontWeight: 800,
          fontSize: "0.78rem",
          fontVariant: "tabular-nums",
        }}
      >
        {fCurrency(amount)}
      </Typography>
    </Box>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export default function InfoStrip({ onFichajesClick }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const width = useResponsive("down", "sm");

  const { lastPayment } = useSelector((state) => state.sessions);
  const { clockings, employees } = useSelector((state) => state.employees);
  const { zonas } = useSelector((state) => state.mesas);

  // Tick every second for live timers
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    const id = setInterval(forceUpdate, 1000);
    return () => clearInterval(id);
  }, []);

  // Include today's active shifts AND unclosed shifts from previous days
  const activeClockings = clockings.filter(
    (u) => u.currentShift && (u.inProgress || !u.currentShift.checkOutTime),
  );
  const hasFichajes = activeClockings.length > 0;
  const hasPayment = !!lastPayment;

  if (!hasFichajes && !hasPayment) return null;

  // ── Last-payment derived data ──
  let method, zonaName, accentColor, paymentIcon;
  if (hasPayment) {
    method = getMethodInfo(lastPayment);
    zonaName = lastPayment.zonaId
      ? zonas.find((z) => z.id === lastPayment.zonaId)?.nombre || null
      : null;
    // Cobros llegados de otro dispositivo (hub payment/{rid}) traen el
    // nombre de zona directamente.
    if (!zonaName && lastPayment.zonaName) zonaName = lastPayment.zonaName;
    accentColor =
      method.type === "card"
        ? theme.palette.info.main
        : method.type === "alt"
          ? theme.palette.secondary.main
          : theme.palette.success.main;
    paymentIcon = method.type === "card" ? creditCardIcon : cashIcon;
  }

  // ── Fichajes derived data ──
  // Exclude unclosed shifts whose scheduled end has passed
  const todayClockings = activeClockings.filter(
    (u) =>
      !(
        !u.currentShift.checkOutTime &&
        u.end &&
        new Date(u.end + "Z") < new Date()
      ),
  );
  const hasOldShifts = todayClockings.length < activeClockings.length;
  const anyOnBreak =
    hasFichajes && todayClockings.some((u) => u.currentShift?.breakInProgress);
  const anyShouldFinish =
    hasFichajes &&
    todayClockings.some((u) => u.currentShift?.shouldServiceBeFinish);
  // Check if any shift exceeds its scheduled duration
  const anyExceeded = todayClockings.some((u) => {
    const h = getShiftHours(u.currentShift.checkInTime);
    const s = getScheduledHours(u);
    return h >= s;
  });
  const anyExceededBadly = todayClockings.some((u) => {
    const h = getShiftHours(u.currentShift.checkInTime);
    const s = getScheduledHours(u);
    return h >= s + 2;
  });
  const fichajesColor = hasOldShifts
    ? theme.palette.warning.main
    : anyShouldFinish || anyExceededBadly
      ? theme.palette.error.main
      : anyOnBreak
        ? "#fa8c16"
        : anyExceeded
          ? theme.palette.warning.main
          : theme.palette.info.main;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: 36,
        px: { xs: 1.5, sm: 2 },
        py: 0.5,
        gap: 1.25,
        overflow: "hidden",
      }}
    >
      {/* ── ÚLTIMO COBRO ─────────────────────────────────────────────── */}
      {hasPayment && !width && (
        <Box
          data-testid="last-payment"
          data-partial={lastPayment.isPartial ? "true" : "false"}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flexShrink: 1,
            overflow: "hidden",
          }}
        >
          {/* icon */}
          <Box sx={{ color: accentColor, display: "flex", flexShrink: 0 }}>
            <Icon icon={checkCircleIcon} width={18} height={18} />
          </Box>

          {/* zona · mesa */}
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            {zonaName && (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {zonaName}
                </Typography>
                <Box
                  component="span"
                  sx={{ color: "text.disabled", fontSize: "0.75rem" }}
                >
                  ·
                </Box>
              </>
            )}
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, whiteSpace: "nowrap", color: accentColor }}
            >
              {lastPayment.mesaName}
            </Typography>
            {lastPayment.isPartial && (
              <Typography
                variant="body2"
                sx={{ opacity: 0.55, whiteSpace: "nowrap" }}
              >
                parcial
              </Typography>
            )}
            {lastPayment.reversed && (
              <Typography
                variant="body2"
                sx={{ color: "error.main", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Anulado
              </Typography>
            )}
          </Box>

          {/* method + amount */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
              color: "text.secondary",
            }}
          >
            <Icon icon={paymentIcon} width={14} height={14} />
            <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
              {method.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, whiteSpace: "nowrap", color: accentColor }}
            >
              {fCurrency(lastPayment.total)}
            </Typography>
          </Box>

          {/* Desglose mixto: dos chips diferenciados (azul tarjeta /
              verde efectivo) junto al total. Sin esto el camarero veía
              "Mixto 29 €" pero no sabía cuánto fue por cada vía — y
              tenía que volver al ticket o a caja para cuadrar. Los
              custom methods se contabilizan dentro de Tarjeta (mismo
              criterio que el resto del flujo de cobro). */}
          {method.type === "mixed" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <MethodChip
                label="Tarjeta"
                amount={lastPayment.card}
                palette={theme.palette.info}
              />
              <MethodChip
                label="Efectivo"
                amount={lastPayment.cash}
                palette={theme.palette.success}
              />
            </Box>
          )}

          {/* efectivo: aportado + cambio */}
          {lastPayment.cash > 0 && lastPayment.cashGiven > 0 && (
            <>
              <Box
                component="span"
                sx={{
                  color: "text.disabled",
                  fontSize: "0.75rem",
                  flexShrink: 0,
                }}
              >
                ·
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                  color: "text.secondary",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.disabled", whiteSpace: "nowrap" }}
                >
                  Aportado
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {fCurrency(lastPayment.cashGiven)}
                </Typography>
              </Box>
              {lastPayment.change > 0 && (
                <>
                  <Box
                    component="span"
                    sx={{
                      color: "text.disabled",
                      fontSize: "0.75rem",
                      flexShrink: 0,
                    }}
                  >
                    ·
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.disabled", whiteSpace: "nowrap" }}
                    >
                      Cambio
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        color: "warning.dark",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fCurrency(lastPayment.change)}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          {/* quién y desde dónde (Fase 1 TPV · cobros de otros dispositivos) */}
          {(lastPayment.employeeName || lastPayment.deviceLabel) && (
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {lastPayment.employeeName || ""}
              {lastPayment.deviceLabel
                ? lastPayment.employeeName
                  ? ` (${lastPayment.deviceLabel})`
                  : lastPayment.deviceLabel
                : ""}
            </Typography>
          )}

          {/* elapsed */}
          <Typography
            variant="body2"
            sx={{ color: "text.disabled", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            {elapsedLabel(lastPayment.timestamp)}
          </Typography>

          {/* dismiss */}
          <IconButton
            size="small"
            onClick={() => dispatch(clearLastPayment())}
            sx={{ p: 0.25, color: "text.disabled", flexShrink: 0 }}
          >
            <Icon icon={closeIcon} width={15} height={15} />
          </IconButton>
        </Box>
      )}

      {/* ── SEPARATOR ────────────────────────────────────────────────── */}
      {hasPayment && hasFichajes && !width && (
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
      )}

      {/* ── FICHAJES ─────────────────────────────────────────────────── */}
      {hasFichajes && !width && (
        <Box
          onClick={onFichajesClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: "auto",
            flexShrink: 0,
            cursor: onFichajesClick ? "pointer" : "default",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            "&:hover": onFichajesClick ? { bgcolor: "action.hover" } : {},
          }}
        >
          {/* pulsing dot */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: fichajesColor,
              flexShrink: 0,
              animation: "stripDot 2s ease-in-out infinite",
              "@keyframes stripDot": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.25 },
              },
            }}
          />

          {/* individual chips */}
          {activeClockings.map((clocking) => {
            const isOldShift =
              !clocking.currentShift.checkOutTime &&
              clocking.end &&
              new Date(clocking.end + "Z") < new Date();
            const style = isOldShift
              ? {
                  bg: "#fff1f0",
                  border: "#ff4d4f88",
                  text: "#cf1322",
                  dot: "#ff4d4f",
                  label: "Sin cerrar",
                }
              : getClockingStyle(clocking);
            const onBreak =
              !isOldShift && clocking.currentShift?.breakInProgress;
            const activeBreak = getActiveBreak(clocking);
            // employeeName puede llegar null en los shifts venidos dentro de
            // todayWorkItems (el mapper de WorkScheduleItem no siempre lo
            // hidrata). Fallback al primer nombre conocido del empleado por
            // employeeId; si tampoco hay, string vacío.
            const empName =
              clocking.currentShift?.employeeName ||
              employees?.find((e) => e.id === clocking.employeeId)?.name ||
              "";
            const firstName = empName.split(" ")[0];
            const entradaStr = new Date(
              clocking.currentShift.checkInTime + "Z",
            ).toLocaleTimeString("es-ES", {
              hour: "2-digit",
              minute: "2-digit",
            });

            const tooltipTitle = isOldShift
              ? `Turno sin cerrar — Entrada: ${entradaStr}`
              : onBreak && activeBreak
                ? `En descanso desde ${new Date(activeBreak.breakStart + "Z").toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })} · Entrada: ${entradaStr}`
                : `Entrada: ${entradaStr}`;

            return (
              <Tooltip key={clocking.id} title={tooltipTitle} arrow>
                <Chip
                  size="small"
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {isOldShift && (
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            color: style.text,
                            lineHeight: 1,
                          }}
                        >
                          ⚠
                        </Typography>
                      )}
                      {onBreak && (
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            bgcolor: style.dot,
                            flexShrink: 0,
                            animation: "breakPulse 1.4s ease-in-out infinite",
                            "@keyframes breakPulse": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0.2 },
                            },
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          color: style.text,
                          lineHeight: 1,
                        }}
                      >
                        {firstName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          color: style.text,
                          opacity: 0.85,
                          lineHeight: 1,
                        }}
                      >
                        {isOldShift
                          ? `Sin cerrar · ${formatDuration(clocking.currentShift.checkInTime)}`
                          : onBreak
                            ? formatBreakDuration(activeBreak?.breakStart)
                            : formatDuration(clocking.currentShift.checkInTime)}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    height: 26,
                    "& .MuiChip-label": { px: 1 },
                    bgcolor: style.bg,
                    border: `1px solid ${style.border}`,
                    borderRadius: "6px",
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
